import { useFormik } from "formik";
import { FC, useCallback, useEffect } from "react";
import * as yup from "yup";
import { Stack, TextField } from "@mui/material";

import { Dialog } from "components/Dialog";
import { Category } from "interfaces/kpis";
import { useKpisCategoriesQuery } from "hooks/queries/kpis";

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (values: FormValuesWithId) => void;
  category: Category;
}

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 2000;

interface FormValues {
  name: string;
}

export interface FormValuesWithId extends FormValues {
  id: number;
}

export const EditCategoryDialog: FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  category
}): JSX.Element => {
  const initialValues: FormValues = {
    name: category.name
  };

  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: FormValues,
      reset: () => void
    ): Promise<void> => {
      if (!isValid) {
        return;
      }
      onConfirm({ id: category.id, ...values });
      reset();
    },
    [category.id, onConfirm]
  );

  const {
    data: categories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError
  } = useKpisCategoriesQuery({ page: 1, size: 999_999 });

  const validationSchema = yup.object({
    name: yup
      .string()
      .required("Name is required")
      .trim()
      .min(
        NAME_MIN_LENGTH,
        `Name must be at least ${NAME_MIN_LENGTH} characters`
      )
      .max(
        NAME_MAX_LENGTH,
        `Name must be less than ${NAME_MAX_LENGTH} characters`
      )
      .matches(
        /^[a-zA-Z0-9\-\s]+$/,
        "Only alphanumeric characters, dashes and spaces allowed"
      )
      .test("unique-name", "Name must be unique", value => {
        if (!value) {
          return true;
        }

        if (isCategoriesLoading || isCategoriesError) {
          return true;
        }

        return !categories.results.some(
          category => category.name.toLowerCase() === value.toLowerCase()
        );
      })
  });

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: values => {
      handleSubmit(formik.isValid, values, () => {});
    }
  });

  const closeAndResetDialog = (): void => {
    onClose();

    setTimeout(() => {
      formik.setErrors({});
      formik.resetForm();
    }, 50);
  };

  // Reset form when dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.setErrors({});
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const shouldDisableConfirm =
    !formik.isValid || (!!Object.keys(formik.touched).length && !formik.dirty);

  return (
    <Dialog
      dialogId="kpi-edit-category-dialog"
      title="Edit Category"
      open={isOpen}
      size="small"
      fullWidth={true}
      onClose={closeAndResetDialog}
      data-qa="KPI-Edit-Category-Dialog"
      confirmDisabled={shouldDisableConfirm}
      confirmText="Save"
      onSubmit={event => {
        event.preventDefault();
        formik.submitForm();
      }}
    >
      <Stack gap={2}>
        <TextField
          variant="outlined"
          fullWidth
          autoFocus
          name="name"
          required
          id="name"
          label="Name"
          data-qa="KPI-Text-Name"
          inputProps={{ maxLength: NAME_MAX_LENGTH }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          value={formik.values.name}
        />
      </Stack>
    </Dialog>
  );
};
